import React from 'react';
import {
  DocumentPlusIcon,
  CalendarIcon,
  DocumentTextIcon,
  CheckBadgeIcon,
  CogIcon,
  ArrowPathIcon,
  ViewColumnsIcon,
  ClockIcon,
  ChartBarIcon,
  UserGroupIcon,
  PencilIcon,
  CheckCircleIcon,
  LightBulbIcon,
  CubeIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  ShoppingCartIcon,
} from '@heroicons/react/24/outline';

// Add CSS for custom scrollbar
const scrollbarStyles = `
  /* Custom scrollbar styles */
  .hide-scrollbar::-webkit-scrollbar {
    height: 4px;
    width: 4px;
  }

  .hide-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }

  .hide-scrollbar::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 10px;
  }

  .hide-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #a0aec0;
  }
`;

export default function CreateProjectHelp() {
  return (
    <div className="min-h-screen bg-cover bg-center bg-gradient-to-b from-indigo-300 via-sky-100 to-white w-full">
      {/* Add custom scrollbar styles */}
      <style dangerouslySetInnerHTML={{ __html: scrollbarStyles }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
              <DocumentPlusIcon className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Project Creation</h1>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Comprehensive project management system for creating, organizing, and tracking
            business projects with AI-powered planning and multi-view task management.
          </p>
        </div>

        {/* System Overview */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6">
            <div className="flex items-center">
              <DocumentPlusIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Project Management Overview</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mb-4">
                  <DocumentPlusIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Project Creation</h3>
                <p className="text-blue-700 text-sm leading-relaxed">
                  Create structured projects with detailed descriptions, objectives,
                  timelines, and associated business processes.
                </p>
              </div>

              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-200">
                <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center mb-4">
                  <LightBulbIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">AI Planning</h3>
                <p className="text-indigo-700 text-sm leading-relaxed">
                  Generate comprehensive project plans and task breakdowns using
                  AI analysis of project requirements and objectives.
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-200">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl flex items-center justify-center mb-4">
                  <ViewColumnsIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Multi-View Management</h3>
                <p className="text-purple-700 text-sm leading-relaxed">
                  View and manage project tasks through Kanban boards, flow diagrams,
                  timeline views, and drag-and-drop interfaces.
                </p>
              </div>

              <div className="bg-gradient-to-br from-pink-50 to-red-50 rounded-2xl p-6 border border-pink-200">
                <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-red-600 rounded-xl flex items-center justify-center mb-4">
                  <ChartBarIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Progress Tracking</h3>
                <p className="text-pink-700 text-sm leading-relaxed">
                  Monitor project progress with real-time statistics, completion rates,
                  and performance analytics across all project phases.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Interface Button Layouts */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-6">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Interface Button Layouts</h2>
            </div>
          </div>

          <div className="p-8">
            {/* Create Project Button */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Project Creation</h3>

              <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-4 border border-gray-200">
                <div className="flex justify-center md:justify-start">
                  <button className="inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm opacity-75 cursor-not-allowed">
                    <PencilIcon className="h-5 w-5 md:h-6 md:w-6" />
                    Create Project
                  </button>
                </div>
              </div>
            </div>

            {/* Project Creation Form */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Project Setup Form</h3>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200 max-w-3xl">
                {/* Form Header */}
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4">
                  <h2 className="text-xl font-bold text-white">Create New Project</h2>
                </div>

                {/* Form Fields */}
                <div className="p-6 space-y-5">
                  {/* Project Type Selection */}
                  <div>
                    <h2 className="text-base md:text-lg font-semibold text-gray-900">Project Type</h2>
                    <div className="mt-2 flex gap-4">
                      <button className="flex-1 px-4 py-3 rounded-md bg-indigo-600 text-white font-semibold opacity-75 cursor-not-allowed">
                        External
                      </button>
                      <button className="flex-1 px-4 py-3 rounded-md bg-gray-200 text-gray-700 font-semibold opacity-75 cursor-not-allowed">
                        Internal
                      </button>
                    </div>
                    <p className="mt-3 text-sm text-gray-600">External projects include customer association, pricing, and materials management.</p>
                  </div>

                  {/* Customer Selection (External Projects Only) */}
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h2 className="text-base md:text-lg font-semibold text-gray-900">Customer</h2>
                    <div className="mt-2">
                      <select
                        className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 opacity-75 cursor-not-allowed"
                      >
                        <option>Select customer</option>
                        <option>Acme Corporation</option>
                        <option>TechStart Inc.</option>
                        <option>Global Solutions Ltd.</option>
                      </select>
                      <p className="mt-2 text-sm text-blue-700">Available for External projects only</p>
                    </div>
                  </div>

                  {/* Project Price (External Projects Only) */}
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h2 className="text-base md:text-lg font-semibold text-gray-900">Project Price (£)</h2>
                    <div className="mt-2">
                      <input
                        type="number"
                        placeholder="Enter project price"
                        className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 opacity-75 cursor-not-allowed"
                      />
                      <p className="mt-2 text-sm text-blue-700">Base project price before materials and labor</p>
                    </div>
                  </div>

                  {/* Project Name */}
                  <div>
                    <h2 className="text-base md:text-lg font-semibold text-gray-900">Project Name</h2>
                    <div className="mt-2">
                      <input
                        type="text"
                        placeholder="Enter project name"
                        className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 opacity-75 cursor-not-allowed"
                      />
                    </div>
                  </div>

                  {/* Project Description */}
                  <div>
                    <h2 className="text-base md:text-lg font-semibold text-gray-900">Description</h2>
                    <div className="mt-2">
                      <textarea
                        rows={6}
                        placeholder="Enter project description"
                        className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 opacity-75 cursor-not-allowed"
                      />
                    </div>
                  </div>

                  {/* Project Objective */}
                  <div>
                    <h2 className="text-base md:text-lg font-semibold text-gray-900">Objective</h2>
                    <div className="mt-2">
                      <textarea
                        rows={6}
                        placeholder="Enter project objective"
                        className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 opacity-75 cursor-not-allowed"
                      />
                    </div>
                  </div>

                  {/* Date Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    <div>
                      <h2 className="text-base md:text-lg font-semibold text-gray-900">Start Date</h2>
                      <div className="mt-2">
                        <input
                          type="date"
                          placeholder="Enter project start date"
                          className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 opacity-75 cursor-not-allowed"
                        />
                      </div>
                    </div>
                    <div>
                      <h2 className="text-base md:text-lg font-semibold text-gray-900">End Date</h2>
                      <div className="mt-2">
                        <input
                          type="date"
                          placeholder="Enter project end date"
                          className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 opacity-75 cursor-not-allowed"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Process Selection */}
                  <div>
                    <h2 className="text-base md:text-lg font-semibold text-gray-900">Process</h2>
                    <div className="mt-2">
                      <select
                        multiple
                        className="block w-full rounded-md bg-white px-3 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 min-h-[120px] opacity-75 cursor-not-allowed"
                      >
                        <option>Select process</option>
                        <option>Customer Service Process</option>
                        <option>Inventory Management</option>
                        <option>Staff Scheduling</option>
                      </select>
                      <p className="mt-3 text-sm text-gray-600">Select one or more processes.</p>
                    </div>
                  </div>

                  {/* Selected Processes Display */}
                  <div className="mt-4 bg-gray-50 p-3 rounded-lg">
                    <h3 className="text-base md:text-lg font-semibold text-gray-900">Selected Processes</h3>
                    <ul className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                      <li className="text-sm text-gray-700 bg-white p-2 rounded-md shadow-sm">
                        Customer Service Process
                      </li>
                      <li className="text-sm text-gray-700 bg-white p-2 rounded-md shadow-sm">
                        Inventory Management
                      </li>
                    </ul>
                  </div>

                  {/* Form Buttons */}
                  <div className="mt-8 flex flex-col-reverse sm:flex-row items-center justify-center sm:justify-end gap-4 sm:gap-x-6">
                    <button className="w-full sm:w-auto text-sm font-semibold text-gray-900 border border-gray-300 rounded-md px-4 py-2 opacity-75 cursor-not-allowed">
                      Cancel
                    </button>
                    <button className="w-full sm:w-auto inline-flex justify-center rounded-md bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-2 text-sm font-semibold text-white shadow-sm opacity-75 cursor-not-allowed">
                      AI Report Creation
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* AI Report Section */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">AI Report Generation</h3>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                <div className="flex flex-col space-y-6 p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <h2 className="text-xl font-semibold text-gray-900">AI Report</h2>
                    <button className="text-sm font-semibold text-gray-900 border border-gray-300 rounded-md px-3 py-1.5 opacity-75 cursor-not-allowed">
                      Close
                    </button>
                  </div>

                  <div className="bg-white p-4 md:p-6 rounded-lg shadow-md">
                    <p className="mt-2 text-sm md:text-base text-gray-700 whitespace-pre-line">
                      Based on your project requirements, here's a comprehensive breakdown of the tasks and timeline...
                    </p>

                    <button className="inline-flex items-center gap-x-2 rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm opacity-75 cursor-not-allowed mt-4">
                      Save Project
                      <CheckBadgeIcon aria-hidden="true" className="-mr-0.5 size-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Project Directory */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Project Directory</h3>

              <div className="bg-white overflow-hidden shadow rounded-lg border border-gray-200">
                <nav aria-label="Directory" className="h-full overflow-y-auto">
                  {/* Letter Section */}
                  <div className="mb-4">
                    <div className="sticky top-0 z-10 border-b bg-gray-50 px-3 py-1.5 text-sm font-semibold text-gray-900">
                      A
                    </div>

                    {/* Project Card */}
                    <div className="w-full flex flex-col md:flex-row justify-between items-start md:items-center mt-3 px-3 py-4 bg-emerald-500 rounded-lg shadow-md text-white cursor-pointer opacity-75">
                      <div className="min-w-0 flex-1">
                        <p className="text-base md:text-sm font-semibold">Annual Review Project</p>
                        <p className="mt-1 truncate text-sm md:text-xs">Comprehensive staff performance review</p>
                        <p className="mt-1 text-sm md:text-xs">Manager Name</p>
                        <div className="flex flex-row gap-4 md:gap-2 mt-2 md:mt-1">
                          <p className="text-sm md:text-xs">
                            <span className="font-semibold">Start:</span> 01/01/2025
                          </p>
                          <p className="text-sm md:text-xs">
                            <span className="font-semibold">End:</span> 31/12/2025
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </nav>
              </div>
            </div>

            {/* Project View Tabs */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Project Task Views</h3>

              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="border-b border-gray-200">
                  <nav className="flex -mb-px overflow-x-auto hide-scrollbar">
                    <button className="whitespace-nowrap py-3 px-4 font-medium border-b-2 border-indigo-500 text-indigo-600 opacity-75 cursor-not-allowed">
                      Kanban
                    </button>
                    <button className="whitespace-nowrap py-3 px-4 font-medium border-b-2 border-transparent text-gray-500 opacity-75 cursor-not-allowed">
                      Gantt
                    </button>
                    <button className="whitespace-nowrap py-3 px-4 font-medium border-b-2 border-transparent text-gray-500 opacity-75 cursor-not-allowed">
                      Flow Chart
                    </button>
                    <button className="whitespace-nowrap py-3 px-4 font-medium border-b-2 border-transparent text-gray-500 opacity-75 cursor-not-allowed">
                      Meetings
                    </button>
                    <button className="whitespace-nowrap py-3 px-4 font-medium border-b-2 border-transparent text-gray-500 opacity-75 cursor-not-allowed">
                      Chat
                    </button>
                    <button className="whitespace-nowrap py-3 px-4 font-medium border-b-2 border-transparent text-emerald-600 bg-emerald-50 opacity-75 cursor-not-allowed">
                      Materials
                    </button>
                  </nav>
                </div>
                <div className="p-4 bg-emerald-50 border-t-2 border-emerald-500">
                  <p className="text-sm text-emerald-700">
                    <strong>Note:</strong> Materials tab is only available for External projects
                  </p>
                </div>
              </div>
            </div>

            {/* Materials Management Interface */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Materials Management (External Projects)</h3>

              {/* Materials Stats Dashboard */}
              <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-lg p-6 border border-emerald-200 mb-6">
                <div className="flex items-center mb-4">
                  <CubeIcon className="h-6 w-6 text-emerald-600 mr-2" />
                  <h4 className="text-lg font-bold text-emerald-900">Materials Overview</h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-sm text-gray-600 mb-2">Used Materials</p>
                    <div className="flex items-baseline">
                      <span className="text-2xl font-bold text-emerald-600">5</span>
                      <span className="text-sm text-gray-500 ml-2">/ 12 total</span>
                    </div>
                    <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-emerald-500 h-2 rounded-full" style={{ width: '42%' }}></div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-sm text-gray-600 mb-2">Remaining</p>
                    <div className="flex items-baseline">
                      <span className="text-2xl font-bold text-blue-600">7</span>
                      <span className="text-sm text-gray-500 ml-2">items left</span>
                    </div>
                    <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-blue-500 h-2 rounded-full" style={{ width: '58%' }}></div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-sm text-gray-600 mb-2">Total Cost</p>
                    <div className="flex items-baseline">
                      <span className="text-2xl font-bold text-indigo-600">£2,450</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Based on unit costs</p>
                  </div>
                </div>
              </div>

              {/* Materials List */}
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-6">
                <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h4 className="text-lg font-semibold text-gray-900">Materials Inventory</h4>
                    <button className="inline-flex items-center gap-x-1.5 rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white opacity-75 cursor-not-allowed">
                      <PencilIcon className="h-4 w-4" />
                      Add Material
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Cost</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Cement Bags</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Construction</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">50</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">£12.50</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center gap-x-1.5 rounded-full bg-emerald-100 px-2 py-1 text-xs font-medium text-emerald-700">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-600"></span>
                            Bought & Used
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <button className="text-indigo-600 hover:text-indigo-900 mr-3 opacity-75 cursor-not-allowed">Edit</button>
                          <button className="text-red-600 hover:text-red-900 opacity-75 cursor-not-allowed">Delete</button>
                        </td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Steel Beams</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Construction</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">25</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">£85.00</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center gap-x-1.5 rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700">
                            <span className="h-1.5 w-1.5 rounded-full bg-blue-600"></span>
                            Bought
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <button className="text-indigo-600 hover:text-indigo-900 mr-3 opacity-75 cursor-not-allowed">Edit</button>
                          <button className="text-red-600 hover:text-red-900 opacity-75 cursor-not-allowed">Delete</button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Add/Edit Material Modal */}
              <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-emerald-600 to-green-600 px-6 py-4">
                  <h4 className="text-xl font-bold text-white">Add Material</h4>
                </div>
                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Material Name</label>
                      <input
                        type="text"
                        placeholder="e.g., Cement Bags"
                        className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 opacity-75 cursor-not-allowed"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                      <input
                        type="text"
                        placeholder="e.g., Construction"
                        className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 opacity-75 cursor-not-allowed"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                      <input
                        type="number"
                        placeholder="50"
                        className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 opacity-75 cursor-not-allowed"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Unit Cost (£)</label>
                      <input
                        type="number"
                        placeholder="12.50"
                        className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 opacity-75 cursor-not-allowed"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Supplier</label>
                    <input
                      type="text"
                      placeholder="e.g., BuildMart Ltd."
                      className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 opacity-75 cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea
                      rows={3}
                      placeholder="Additional notes about the material..."
                      className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 opacity-75 cursor-not-allowed"
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center">
                      <input type="checkbox" className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 opacity-75 cursor-not-allowed" />
                      <span className="ml-2 text-sm text-gray-700">Mark as Bought</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 opacity-75 cursor-not-allowed" />
                      <span className="ml-2 text-sm text-gray-700">Mark as Used</span>
                    </label>
                  </div>
                  <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                    <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md opacity-75 cursor-not-allowed">
                      Cancel
                    </button>
                    <button className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-md opacity-75 cursor-not-allowed">
                      Save Material
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Feature Explanations */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
              <div className="bg-white rounded-xl p-6 border border-blue-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">1</span>
                  </div>
                  <h4 className="text-lg font-bold text-blue-900">Project Types</h4>
                </div>
                <p className="text-blue-700 text-sm leading-relaxed">
                  Choose between External projects (with customer association, pricing, and materials) 
                  or Internal projects (standard task management without external billing).
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-indigo-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">2</span>
                  </div>
                  <h4 className="text-lg font-bold text-indigo-900">Customer Association</h4>
                </div>
                <p className="text-indigo-700 text-sm leading-relaxed">
                  Link External projects to customer records for integrated CRM, 
                  invoice generation, and comprehensive client project tracking.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-purple-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">3</span>
                  </div>
                  <h4 className="text-lg font-bold text-purple-900">AI Analysis</h4>
                </div>
                <p className="text-purple-700 text-sm leading-relaxed">
                  Generate detailed project breakdowns and task lists using AI analysis
                  of project requirements, objectives, and selected processes.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-pink-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-pink-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">4</span>
                  </div>
                  <h4 className="text-lg font-bold text-pink-900">Materials Management</h4>
                </div>
                <p className="text-pink-700 text-sm leading-relaxed">
                  Track materials inventory for External projects with quantity management, 
                  cost tracking, supplier information, and bought/used status indicators.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-emerald-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">5</span>
                  </div>
                  <h4 className="text-lg font-bold text-emerald-900">Task Visualization</h4>
                </div>
                <p className="text-emerald-700 text-sm leading-relaxed">
                  Organize and manage project tasks through Kanban boards, Gantt charts, 
                  flow diagrams, and timeline views for comprehensive project oversight.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-yellow-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-yellow-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">6</span>
                  </div>
                  <h4 className="text-lg font-bold text-yellow-900">Progress Tracking</h4>
                </div>
                <p className="text-yellow-700 text-sm leading-relaxed">
                  Monitor project progress with real-time statistics, completion rates,
                  daily progress metrics, and overall performance analytics.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-teal-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">7</span>
                  </div>
                  <h4 className="text-lg font-bold text-teal-900">Team Collaboration</h4>
                </div>
                <p className="text-teal-700 text-sm leading-relaxed">
                  Facilitate team communication through integrated chat functionality,
                  meeting scheduling, and collaborative task management.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-orange-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">8</span>
                  </div>
                  <h4 className="text-lg font-bold text-orange-900">Process Integration</h4>
                </div>
                <p className="text-orange-700 text-sm leading-relaxed">
                  Link projects to existing business processes for consistent task
                  generation and workflow management across the organization.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-red-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">9</span>
                  </div>
                  <h4 className="text-lg font-bold text-red-900">Cost Tracking</h4>
                </div>
                <p className="text-red-700 text-sm leading-relaxed">
                  Monitor project costs through base pricing, materials expenses, and 
                  automated invoice generation for accurate financial management.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-8 py-6">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Project Creation Best Practices</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                  <h3 className="text-lg font-bold text-blue-900 mb-3">Project Type Selection</h3>
                  <ul className="space-y-2 text-sm text-blue-700">
                    <li>• Choose External for client projects requiring invoicing</li>
                    <li>• Use External when materials tracking is needed</li>
                    <li>• Select Internal for operational/administrative projects</li>
                    <li>• Link External projects to customer records immediately</li>
                    <li>• Set accurate project pricing for invoice generation</li>
                  </ul>
                </div>

                <div className="bg-indigo-50 rounded-xl p-6 border border-indigo-200">
                  <h3 className="text-lg font-bold text-indigo-900 mb-3">Planning & Setup</h3>
                  <ul className="space-y-2 text-sm text-indigo-700">
                    <li>• Define clear, measurable project objectives</li>
                    <li>• Set realistic start and end dates with buffer time</li>
                    <li>• Select relevant business processes for task generation</li>
                    <li>• Write detailed descriptions for AI analysis</li>
                    <li>• Consider resource availability and team capacity</li>
                  </ul>
                </div>

                <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                  <h3 className="text-lg font-bold text-purple-900 mb-3">Materials Management</h3>
                  <ul className="space-y-2 text-sm text-purple-700">
                    <li>• Add materials before project starts for accurate tracking</li>
                    <li>• Include supplier information for procurement records</li>
                    <li>• Mark materials as "Bought" when purchased</li>
                    <li>• Mark as "Used" when consumed in project tasks</li>
                    <li>• Only used materials appear on customer invoices</li>
                    <li>• Monitor materials dashboard for cost oversight</li>
                  </ul>
                </div>

                <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                  <h3 className="text-lg font-bold text-pink-900 mb-3">AI Report Utilization</h3>
                  <ul className="space-y-2 text-sm text-pink-700">
                    <li>• Review AI-generated reports for completeness</li>
                    <li>• Modify task breakdowns as needed for your team</li>
                    <li>• Ensure all critical project milestones are included</li>
                    <li>• Validate task assignments and time estimates</li>
                    <li>• Save and finalize only after thorough review</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-200">
                  <h3 className="text-lg font-bold text-emerald-900 mb-3">Task Management</h3>
                  <ul className="space-y-2 text-sm text-emerald-700">
                    <li>• Use multiple view modes for different planning needs</li>
                    <li>• Switch between Kanban, Gantt, and Flow views</li>
                    <li>• Regularly update task status and progress</li>
                    <li>• Monitor project timelines and adjust as needed</li>
                    <li>• Track completion rates and identify bottlenecks</li>
                  </ul>
                </div>

                <div className="bg-teal-50 rounded-xl p-6 border border-teal-200">
                  <h3 className="text-lg font-bold text-teal-900 mb-3">Customer Association</h3>
                  <ul className="space-y-2 text-sm text-teal-700">
                    <li>• Always link External projects to customer records</li>
                    <li>• Verify customer details before project creation</li>
                    <li>• Use customer CRM for contact information</li>
                    <li>• Materials and pricing flow to customer invoices</li>
                    <li>• Maintain project history per customer</li>
                  </ul>
                </div>

                <div className="bg-orange-50 rounded-xl p-6 border border-orange-200">
                  <h3 className="text-lg font-bold text-orange-900 mb-3">Cost Management</h3>
                  <ul className="space-y-2 text-sm text-orange-700">
                    <li>• Set accurate base project pricing upfront</li>
                    <li>• Track all material costs with unit pricing</li>
                    <li>• Monitor materials dashboard for budget control</li>
                    <li>• Only used materials contribute to final invoice</li>
                    <li>• Review total costs before invoice generation</li>
                  </ul>
                </div>

                <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                  <h3 className="text-lg font-bold text-red-900 mb-3">Team Coordination</h3>
                  <ul className="space-y-2 text-sm text-red-700">
                    <li>• Keep team informed through regular updates</li>
                    <li>• Use chat for quick questions and clarifications</li>
                    <li>• Schedule meetings through the Meetings tab</li>
                    <li>• Share progress reports with stakeholders</li>
                    <li>• Document lessons learned for future projects</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}